import * as R from 'ramda';
import { ComponentIds, HierarchyUtils, MeasurementUtils } from '@inter-ikea-core/core';
import { EntityApi, transformOuterMeasurements, OuterMeasurementsComponent, PartKey } from '@inter-ikea-storageone/range';
export class PaxEntityApi extends EntityApi {
    /**
     * @override Corner solutions requires more complex calculations
     */
    getOuterMeasurementsPerWall() {
        const entities = this.getByComponent(OuterMeasurementsComponent.id);
        if (entities.length === 0) {
            return null;
        }
        const grouped = entities.reduce((acc, entity) => {
            const parentComponent = entity.getComponent(ComponentIds.PARENT_COMPONENT_ID);
            if (parentComponent) {
                const hierarchy = HierarchyUtils.hierarchy(entity);
                const addOnFrame = hierarchy.find(e => e.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID) &&
                    e.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
                        ?.partKey === 'frame');
                const frame = hierarchy.find(e => e.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID) &&
                    e.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
                        ?.partKey === 'normal-frame');
                if (addOnFrame && frame) {
                    const addOnFrameWall = this.getEntityWall(addOnFrame);
                    const frameWall = this.getEntityWall(frame);
                    return {
                        ...acc,
                        [addOnFrameWall]: [...acc[addOnFrameWall], addOnFrame],
                        [frameWall]: [...acc[frameWall], frame]
                    };
                }
                else {
                    const wall = this.getEntityWall(entity);
                    return {
                        ...acc,
                        [wall]: R.uniqBy(R.prop('id'), [...acc[wall], entity])
                    };
                }
            }
            const wall = this.getEntityWall(entity);
            return {
                ...acc,
                [wall]: R.uniqBy(R.prop('id'), [...acc[wall], entity])
            };
        }, { front: [], left: [], right: [], back: [] });
        const getDepth = (frameEntity, entities, sideWall = false) => {
            const frameMeasures = frameEntity.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            const otherEntities = entities.filter(e => e.id !== frameEntity.id);
            const otherEntitiesMeasures = this.getMeasurements(otherEntities);
            const otherEntitiesDepth = sideWall
                ? otherEntitiesMeasures?.width
                : otherEntitiesMeasures?.depth;
            return Math.max(otherEntitiesDepth || 0, frameMeasures?.depth || 0);
        };
        const getNormalFrameFromAddOnEntity = (addOnFrame) => {
            const addOnFrameAssembly = addOnFrame.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent;
            return (addOnFrameAssembly?.getComponent(ComponentIds.CHILD_COMPONENT_ID)?.parent ||
                null);
        };
        const getMeasurementsWithOutNormalFrameParent = (addOnFrame, entities) => {
            const entitiesList = [];
            const addOnFrames = this.getEntitiesWithPartKey(entities, 'frame');
            addOnFrames.forEach(addOnFrame => {
                const normalFrameParent = getNormalFrameFromAddOnEntity(addOnFrame);
                if (normalFrameParent) {
                    entitiesList.push(normalFrameParent);
                }
            });
            const otherEntities = entities.filter(entity => !addOnFrames.includes(entity));
            entitiesList.push(...otherEntities);
            return this.getMeasurements(entitiesList, true);
        };
        const adjustFrontFrameForMultiWallAssembly = (normalFrame, wallEntities, wall, measurements) => {
            const sideWall = wall === 'left' || wall === 'right' ? true : false;
            const depth = getDepth(normalFrame, wallEntities, sideWall);
            const cornerSolution = HierarchyUtils.parent(normalFrame);
            const isMirrored = cornerSolution ? this.isMirrored(cornerSolution) : false;
            const cornerFrame = HierarchyUtils.findInHierarchy(normalFrame, entity => entity.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
                ?.partKey === PartKey.CORNER_SOLUTION_CORNER_FRAME);
            const cornerFrameDepth = cornerFrame?.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size.depth;
            // Corner frames are mounted against the regular frame creating a gap to the wall that is different depending on corner frame depth
            const cornerFrameGap = cornerFrameDepth === 350 ? 131.5 : 106.5;
            switch (wall) {
                case 'front':
                    return {
                        ...measurements,
                        width: measurements.width + cornerFrameGap,
                        depth: depth,
                        min: {
                            ...measurements.min,
                            x: isMirrored ? measurements.min.x : measurements.min.x - cornerFrameGap
                        },
                        max: {
                            ...measurements.max,
                            z: depth,
                            x: isMirrored ? measurements.max.x + cornerFrameGap : measurements.max.x
                        }
                    };
                case 'left':
                    return {
                        ...measurements,
                        width: measurements.width + cornerFrameGap,
                        depth: depth,
                        min: {
                            ...measurements.min,
                            z: isMirrored ? measurements.min.z - cornerFrameGap : measurements.min.z
                        },
                        max: {
                            ...measurements.max,
                            x: measurements.min.x + depth,
                            z: isMirrored ? measurements.max.z : measurements.max.z + cornerFrameGap
                        }
                    };
                case 'right':
                    return {
                        ...measurements,
                        width: measurements.width + cornerFrameGap,
                        depth: depth,
                        min: {
                            ...measurements.min,
                            x: measurements.max.x - depth,
                            z: isMirrored ? measurements.min.z : measurements.min.z - cornerFrameGap
                        },
                        max: {
                            ...measurements.max,
                            z: isMirrored ? measurements.max.z + cornerFrameGap : measurements.max.z
                        }
                    };
                case 'back':
                    return {
                        ...measurements,
                        width: measurements.width + cornerFrameGap,
                        depth: depth,
                        min: {
                            ...measurements.min,
                            x: isMirrored ? measurements.min.x - cornerFrameGap : measurements.min.x,
                            z: measurements.max.z - depth
                        },
                        max: {
                            ...measurements.max,
                            x: isMirrored ? measurements.max.x : measurements.max.x + cornerFrameGap
                        }
                    };
            }
        };
        const adjustAddOnFrameForMultiWallAssembly = (addOnFrame, wallEntities, wall, measurements) => {
            const actualMeasurements = getMeasurementsWithOutNormalFrameParent(addOnFrame, wallEntities) || measurements;
            switch (wall) {
                case 'front':
                    const depth = getDepth(addOnFrame, wallEntities, false);
                    return {
                        ...measurements,
                        depth: depth,
                        width: actualMeasurements.width,
                        min: {
                            ...measurements.min,
                            x: actualMeasurements.min.x
                        },
                        max: {
                            ...measurements.max,
                            x: actualMeasurements.max.x,
                            z: depth
                        }
                    };
                case 'left':
                case 'right':
                    return {
                        ...measurements,
                        width: actualMeasurements.depth,
                        min: {
                            ...measurements.min,
                            z: actualMeasurements.min.z
                        },
                        max: {
                            ...measurements.max,
                            z: actualMeasurements.max.z
                        }
                    };
                case 'back':
                    return {
                        ...measurements,
                        width: actualMeasurements.width,
                        min: {
                            ...measurements.min,
                            x: actualMeasurements.min.x
                        },
                        max: {
                            ...measurements.max,
                            x: actualMeasurements.max.x
                        }
                    };
            }
        };
        const adjustForMultiWallAssembly = (wallEntities, wall, measurements) => {
            const normalFrames = this.getEntitiesWithPartKey(wallEntities, 'normal-frame');
            const addOnFrames = this.getEntitiesWithPartKey(wallEntities, 'frame');
            let newMeasurements = { ...measurements };
            normalFrames.forEach(frame => {
                newMeasurements = adjustFrontFrameForMultiWallAssembly(frame, wallEntities, wall, newMeasurements);
            });
            addOnFrames.forEach(frame => {
                newMeasurements = adjustAddOnFrameForMultiWallAssembly(frame, wallEntities, wall, newMeasurements);
            });
            return newMeasurements;
        };
        return Object.entries(grouped).reduce((acc, [wall, entities]) => {
            const outsideMeasurements = MeasurementUtils.getOutsideMeasurementWithoutHierarchies(entities);
            if (!outsideMeasurements) {
                return acc;
            }
            const transformedMeasurements = transformOuterMeasurements(outsideMeasurements, wall === 'left' || wall === 'right');
            if (transformedMeasurements === null) {
                return acc;
            }
            const measurements = adjustForMultiWallAssembly(entities, wall, transformedMeasurements);
            return {
                ...acc,
                [wall]: measurements
            };
        }, {});
    }
}
//# sourceMappingURL=PaxEntityApi.js.map